import { useEffect, useState } from "react";
import type { ApiClient } from "../../api/client";
import type { BridgeHealth } from "../../api/types";
import { t } from "../../i18n";

/** How often the till asks: the add-on beats every half minute, Tafo warns after 3 minutes. */
const POLL_MS = 60_000;

/**
 * Is a bridge of the shop alive? (Furkan, 23/10/2026: at Pizza Sara the Live Orders page of Takeaway hung from 18:53
 * on and nobody saw it.) Asked every minute; only bridges in use come back, so a shop without one never sees
 * anything. A failed request keeps the last answer.
 */
export function useBridgeHealth(api: ApiClient, on: boolean): BridgeHealth[] {
  const [bridges, setBridges] = useState<BridgeHealth[]>([]);
  useEffect(() => {
    if (!on) return;
    let stopped = false;
    const load = () => {
      api
        .request<{ bridges: BridgeHealth[] }>("GET", "/device/bridges/health", { retry: true })
        .then((answer) => {
          if (!stopped) setBridges(answer.bridges);
        })
        // extra information: the till works without it, and the next poll tries again
        .catch(() => undefined);
    };
    load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [api, on]);
  return bridges;
}

const hour = (iso: string | null) =>
  iso === null ? "?" : new Date(iso).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" });

/** The till's warnings: "⚠ Takeaway-brug: geen contact sinds 18:53 …", one per bridge that is not right. */
export function bridgeWarnings(bridges: readonly BridgeHealth[]): string[] {
  const lines: string[] = [];
  for (const health of bridges) {
    const name = t.bridges.name(health.bridge);
    const page = t.bridges.page(health.bridge);
    if (health.problem === "silent") lines.push(t.bridges.silent(name, page, hour(health.since)));
    else if (health.problem === "page" && health.page === "login")
      lines.push(t.bridges.login(name, page, hour(health.since)));
    else if (health.problem === "page") lines.push(t.bridges.hanging(name, page, hour(health.since)));
    else if (health.waiting > 0) lines.push(t.bridges.waiting(name, health.waiting));
  }
  return lines;
}
